import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ScriptCard from "@/components/ScriptCard";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Server, ScanLine, PlayCircle, RefreshCw, Loader2, Lock, Trash2, ShieldQuestion,
  Clock, CheckCircle2, AlertTriangle, CheckCheck, FileText, ExternalLink, Wand2, KeyRound, Compass,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", product: "sap_successfactors", env_url: "", username: "", password: "",
  api_token: "", test_data: "", use_dummy_data: false,
  schedule: { frequency: "weekly", time: "02:00", day: "Monday" },
};

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const RISK = { High: "#E5325F", Medium: "#FF8A3D", Low: "#0F9D77" };

export default function Config() {
  const [envs, setEnvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [activeRun, setActiveRun] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/environments").then(({ data }) => {
      setEnvs(data);
      setSelected(data[0] || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) { setScripts([]); setReleases([]); setActiveRun(null); return; }
    api.get(`/environments/${selected.id}/scripts`).then((r) => setScripts(r.data)).catch(() => {});
    api.get(`/release-updates`).then((r) => setReleases(r.data.filter((x) => x.environment_id === selected.id))).catch(() => {});
    api.get(`/agent-runs`, { params: { environment_id: selected.id, status: "running" } })
      .then((r) => setActiveRun(r.data[0] || null)).catch(() => {});
  }, [selected]);

  useEffect(() => {
    if (!activeRun || activeRun.status !== "running") return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/agent-runs/${activeRun.id}`);
        setActiveRun(data);
        if (data.status !== "running") {
          if (data.status === "completed") {
            toast[data.release_ready ? "success" : "warning"](`Run complete — ${data.pass_rate}% pass`, {
              description: `${data.passed} passed · ${data.failed} failed · ${data.blocked} blocked`,
              action: { label: "View report", onClick: () => navigate(`/runs/${data.id}`) },
            });
          } else toast.error("Run failed", { description: data.error });
          const list = await api.get("/environments");
          setEnvs(list.data); setSelected(list.data.find((e) => e.id === selected?.id) || null);
          const s = await api.get(`/environments/${selected.id}/scripts`); setScripts(s.data);
        }
      } catch { setActiveRun(null); }
    }, 3000);
    return () => clearTimeout(t);
  }, [activeRun, selected, navigate]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setSched = (k) => (v) => setForm({ ...form, schedule: { ...form.schedule, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/environments", form);
      toast.success("Environment configured. Secrets encrypted at rest.");
      setOpen(false); setForm(emptyForm);
      const list = await api.get("/environments");
      setEnvs(list.data); setSelected(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const refreshSelected = async () => {
    const { data } = await api.get("/environments");
    setEnvs(data);
    setSelected(data.find((e) => e.id === selected.id) || null);
  };

  const generate = async () => {
    setBusy("gen");
    try {
      const { data } = await api.post(`/environments/${selected.id}/generate-scripts`);
      setScripts(data.scripts);
      toast.success(`${data.scripts.length} test scripts generated.`, { description: data.scan.note });
      await refreshSelected();
    } catch { toast.error("Generation failed"); } finally { setBusy(""); }
  };

  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.post(`/environments/${selected.id}/run`);
      setActiveRun(data);
      toast.info("Agents launched a live browser run.", { description: `${data.total} approved scripts queued` });
    } catch (err) { toast.error(err.response?.data?.detail || "Run failed"); } finally { setBusy(""); }
  };

  const reloadScripts = async () => {
    const s = await api.get(`/environments/${selected.id}/scripts`);
    setScripts(s.data);
  };

  const updateScript = async (id, patch) => {
    try {
      const { data } = await api.put(`/environments/${selected.id}/scripts/${id}`, patch);
      setScripts((prev) => prev.map((x) => (x.id === id ? data : x)));
      if (patch.status) toast.success(patch.status === "approved" ? "Script approved for execution." : `Script ${patch.status}.`);
      else toast.success("Script updated.");
      refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const deleteScript = async (id) => {
    await api.delete(`/environments/${selected.id}/scripts/${id}`);
    setScripts((prev) => prev.filter((x) => x.id !== id));
    refreshSelected();
  };

  const approveAll = async () => {
    setBusy("approve");
    try {
      const { data } = await api.post(`/environments/${selected.id}/scripts/approve-all`);
      toast.success(`${data.approved} scripts approved.`);
      await reloadScripts(); await refreshSelected();
    } catch { toast.error("Approval failed"); } finally { setBusy(""); }
  };

  const analyze = async () => {
    setBusy("rel");
    try {
      const { data } = await api.post(`/environments/${selected.id}/release-analysis`);
      setReleases([data, ...releases]);
      const n = data.flagged_scripts?.length || 0;
      toast.success(`Release analysed: ${data.release_name}`, {
        description: `${data.impacted_modules.length} impacted modules · ${n} script${n === 1 ? "" : "s"} flagged for self-healing`,
      });
      await reloadScripts();
    } catch { toast.error("Analysis failed"); } finally { setBusy(""); }
  };

  const loginMatrix = async () => {
    setBusy("matrix");
    try {
      const { data } = await api.post(`/environments/${selected.id}/generate-login-suite`);
      toast.success(`${data.count} login combinations generated`, {
        description: `Detected: ${data.form.username} · ${data.form.password} · ${data.form.submit}${data.note ? ` — ${data.note}` : ""}`,
      });
      await reloadScripts(); await refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || "Login matrix failed"); } finally { setBusy(""); }
  };

  const explore = async () => {
    setBusy("explore");
    try {
      const { data } = await api.post(`/environments/${selected.id}/generate-post-login`, {}, { timeout: 420000 });
      toast.success(`${data.count} post-login tests generated`, { description: `${data.scan.title} — ${data.scan.element_count} elements mapped after sign-in` });
      await reloadScripts(); await refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || "Exploration failed"); } finally { setBusy(""); }
  };

  const heal = async () => {
    setBusy("heal");
    try {
      const { data } = await api.post(`/environments/${selected.id}/self-heal`);
      toast.success(`${data.healed.length} script${data.healed.length === 1 ? "" : "s"} rebuilt by the agents.`, { description: "Review and re-approve them before the next run." });
      await reloadScripts(); await refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || "Self-heal failed"); } finally { setBusy(""); }
  };

  const remove = async (id) => {
    await api.delete(`/environments/${id}`);
    toast.success("Environment removed");
    const list = await api.get("/environments");
    setEnvs(list.data); setSelected(list.data[0] || null);
  };

  const approvedCount = scripts.filter((s) => s.status === "approved").length;
  const pendingCount = scripts.filter((s) => !s.status || s.status === "draft").length;
  const healCount = scripts.filter((s) => s.impact_flag || s.last_status === "failed").length;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="config-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline text-[#7C3AED] mb-2">Configuration</p>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Test environments</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-env-btn" className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
              <Plus className="w-4 h-4" /> New environment
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="env-dialog">
            <DialogHeader><DialogTitle className="font-heading">Configure test environment</DialogTitle><DialogDescription className="text-xs text-secondary">Credentials and test data are encrypted at rest and only used by your agents.</DialogDescription></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs text-secondary">Environment name</Label>
                <Input value={form.name} onChange={setF("name")} data-testid="env-name-input" className="rounded-sm bg-background border-border mt-1.5" placeholder="Production SF Test Tenant" />
              </div>
              <div>
                <Label className="text-xs text-secondary">SAP platform</Label>
                <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                  <SelectTrigger className="rounded-sm bg-background border-border mt-1.5" data-testid="env-product-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="sap_successfactors">SAP SuccessFactors</SelectItem>
                    <SelectItem value="sap_public_cloud">SAP Public Cloud</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-secondary">Test environment URL</Label>
                <Input value={form.env_url} onChange={setF("env_url")} data-testid="env-url-input" className="rounded-sm bg-background border-border mt-1.5 font-mono text-sm" placeholder="https://tenant.successfactors.eu/..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-secondary flex items-center gap-1"><Lock className="w-3 h-3" /> Username</Label>
                  <Input value={form.username} onChange={setF("username")} data-testid="env-username-input" className="rounded-sm bg-background border-border mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs text-secondary flex items-center gap-1"><Lock className="w-3 h-3" /> Password</Label>
                  <Input type="password" value={form.password} onChange={setF("password")} data-testid="env-password-input" className="rounded-sm bg-background border-border mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-secondary flex items-center gap-1"><Lock className="w-3 h-3" /> API token (optional)</Label>
                <Input value={form.api_token} onChange={setF("api_token")} data-testid="env-token-input" className="rounded-sm bg-background border-border mt-1.5 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs text-secondary flex items-center gap-1"><Lock className="w-3 h-3" /> Test data (encrypted)</Label>
                <Textarea value={form.test_data} onChange={setF("test_data")} data-testid="env-testdata-input" className="rounded-sm bg-background border-border mt-1.5 font-mono text-xs" rows={3} placeholder='{"employee_id":"1001","country":"AU"}' />
              </div>
              <div className="flex items-center justify-between border border-border p-3 rounded-sm">
                <div className="flex items-center gap-2 text-sm"><ShieldQuestion className="w-4 h-4 text-primary" /> Let agents generate dummy data</div>
                <Switch checked={form.use_dummy_data} onCheckedChange={(v) => setForm({ ...form, use_dummy_data: v })} data-testid="env-dummy-switch" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-secondary">Schedule</Label>
                  <Select value={form.schedule.frequency} onValueChange={setSched("frequency")}>
                    <SelectTrigger className="rounded-sm bg-background border-border mt-1.5" data-testid="env-freq-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-secondary">Time</Label>
                  <Input value={form.schedule.time} onChange={(e) => setSched("time")(e.target.value)} className="rounded-sm bg-background border-border mt-1.5" data-testid="env-time-input" />
                </div>
                <div>
                  <Label className="text-xs text-secondary">{form.schedule.frequency === "monthly" ? "Day of month" : "Day"}</Label>
                  {form.schedule.frequency === "weekly" ? (
                    <Select value={WEEKDAYS.includes(form.schedule.day) ? form.schedule.day : "Monday"} onValueChange={setSched("day")}>
                      <SelectTrigger className="rounded-sm bg-background border-border mt-1.5" data-testid="env-day-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={form.schedule.frequency === "monthly" ? "number" : "text"} min={1} max={31}
                      disabled={form.schedule.frequency !== "monthly"} value={form.schedule.frequency === "monthly" ? form.schedule.day : "—"}
                      onChange={(e) => setSched("day")(e.target.value)} className="rounded-sm bg-background border-border mt-1.5" data-testid="env-day-input" />
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={save} disabled={saving || !form.name} data-testid="env-save-btn" className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold w-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & encrypt"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {envs.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <Server className="w-10 h-10 text-secondary mx-auto mb-4" />
          <p className="text-secondary">No environments yet. Create one to deploy your testing agents.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* list */}
          <div className="space-y-3">
            {envs.map((e) => (
              <button key={e.id} onClick={() => setSelected(e)} data-testid={`env-item-${e.id}`}
                className={`w-full text-left border p-4 hover-lift transition-colors ${selected?.id === e.id ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{e.name}</span>
                  <Badge className="rounded-sm bg-muted text-secondary text-[10px]">{e.product === "sap_successfactors" ? "SF" : "PC"}</Badge>
                </div>
                <div className="text-xs text-secondary mt-1 font-mono truncate">{e.env_url || "no url"}</div>
                <div className="flex items-center gap-3 mt-3 text-[11px] text-secondary">
                  <span><ScanLine className="w-3 h-3 inline mr-1" />{e.script_count || 0} scripts</span>
                  {e.last_pass_rate != null && <span className={e.last_pass_rate >= 85 ? "text-[#0F9D77]" : "text-[#FF8A3D]"}>{e.last_pass_rate}% pass</span>}
                </div>
              </button>
            ))}
          </div>

          {/* detail */}
          <div className="lg:col-span-2">
            {selected && (
              <div className="card-soft">
                <div className="p-6 border-b border-border flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-bold text-xl">{selected.name}</h3>
                    <p className="text-xs text-secondary font-mono">{selected.env_url}</p>
                  </div>
                  <button onClick={() => remove(selected.id)} data-testid="env-delete-btn" className="text-secondary hover:text-[#E5325F] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>

                <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 border-b border-border">
                  <Button onClick={generate} disabled={!!busy || !!activeRun} data-testid="generate-scripts-btn" title="Scan the environment URL in a live browser and let the AI draft regression tests" className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
                    {busy === "gen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />} Scan & generate
                  </Button>
                  <Button onClick={loginMatrix} disabled={!!busy || !!activeRun} data-testid="login-matrix-btn" variant="outline"
                    title="Detect the login form and generate every username × password combination with test data"
                    className="rounded-sm border-[#2A1E9E]/30 text-[#2A1E9E] gap-2">
                    {busy === "matrix" ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4 text-[#2A1E9E]" />} Login test matrix
                  </Button>
                  <Button onClick={explore} disabled={!!busy || !!activeRun || !selected.password_set} data-testid="explore-post-login-btn" variant="outline"
                    title={selected.password_set ? "Sign in and generate tests for the landing page (tiles, menus, search)" : "Add username & password to explore beyond the login screen"}
                    className="rounded-sm border-[#7C3AED]/30 text-[#7C3AED] gap-2">
                    {busy === "explore" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass className="w-4 h-4" />} Explore after login
                  </Button>
                  <Button onClick={run} disabled={!!busy || !!activeRun || approvedCount === 0} data-testid="run-agent-btn" variant="outline"
                    title={approvedCount === 0 ? "Approve at least one script first" : "Execute approved scripts in a live browser"}
                    className="rounded-sm border-border gap-2">
                    {busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4 text-[#0F9D77]" />} Run agent ({approvedCount})
                  </Button>
                  <Button onClick={analyze} disabled={!!busy} data-testid="release-analysis-btn" title="Analyse the latest SAP release and flag impacted scripts" variant="outline" className="rounded-sm border-border gap-2">
                    {busy === "rel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-[#FF8A3D]" />} Release impact
                  </Button>
                  <Button onClick={heal} disabled={!!busy || !!activeRun || healCount === 0} data-testid="self-heal-btn" variant="outline"
                    title={healCount === 0 ? "No failed or release-impacted scripts to rebuild" : "Agents rebuild failed / impacted scripts for re-approval"}
                    className="rounded-sm border-border gap-2">
                    {busy === "heal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 text-[#7C3AED]" />} Self-heal ({healCount})
                  </Button>
                </div>

                {activeRun && (
                  <div className="px-6 py-4 border-b border-border bg-[#7C3AED]/5" data-testid="active-run-panel">
                    <div className="flex items-center gap-3 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#7C3AED]" />
                      <span className="text-sm font-semibold text-[#1E1560]">Live browser run in progress</span>
                      <span className="ml-auto font-mono text-xs text-secondary">{activeRun.progress?.current || 0}/{activeRun.progress?.total || 0}</span>
                    </div>
                    <Progress value={activeRun.progress?.total ? (activeRun.progress.current / activeRun.progress.total) * 100 : 0} className="h-1.5 bg-muted" />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-secondary truncate">Now: {activeRun.progress?.script}</p>
                      <button onClick={() => navigate(`/runs/${activeRun.id}`)} className="text-xs text-[#3A2DB8] hover:text-[#FF6B3D] flex items-center gap-1" data-testid="active-run-open">
                        Watch live <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}

                {!activeRun && selected.last_run_id && (
                  <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2 text-secondary">
                      {selected.last_pass_rate >= 85 ? <CheckCircle2 className="w-4 h-4 text-[#0F9D77]" /> : <AlertTriangle className="w-4 h-4 text-[#FF8A3D]" />}
                      Last run {selected.last_run_at?.slice(0, 16).replace("T", " ")} UTC · {selected.last_pass_rate}% pass
                    </span>
                    <button onClick={() => navigate(`/runs/${selected.last_run_id}`)} className="text-[#3A2DB8] hover:text-[#FF6B3D] flex items-center gap-1 font-medium" data-testid="last-run-report-btn">
                      <FileText className="w-4 h-4" /> View report
                    </button>
                  </div>
                )}

                <Tabs defaultValue="scripts" className="p-6">
                  <TabsList className="bg-background rounded-sm">
                    <TabsTrigger value="scripts" data-testid="tab-scripts" className="rounded-sm">Scripts ({scripts.length})</TabsTrigger>
                    <TabsTrigger value="release" data-testid="tab-release" className="rounded-sm">Release impact ({releases.length})</TabsTrigger>
                    <TabsTrigger value="config" data-testid="tab-config" className="rounded-sm">Configuration</TabsTrigger>
                  </TabsList>

                  <TabsContent value="scripts" className="mt-6 space-y-3">
                    {scripts.length > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-muted/60 border border-border" data-testid="approval-bar">
                        <p className="text-xs text-secondary">
                          <span className="font-semibold text-[#1E1560]">Human approval gate:</span> {approvedCount} of {scripts.length} approved · {pendingCount} awaiting review. Only approved scripts execute.
                        </p>
                        {pendingCount > 0 && (
                          <Button size="sm" onClick={approveAll} disabled={busy === "approve"} data-testid="approve-all-btn"
                            className="rounded-full h-8 bg-[#0F9D77] hover:bg-[#0B7F60] text-white gap-1">
                            {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />} Approve all
                          </Button>
                        )}
                      </div>
                    )}
                    {scripts.length === 0 ? (
                      <p className="text-secondary text-sm py-8 text-center">No scripts yet. Click "Scan &amp; generate".</p>
                    ) : scripts.map((s) => (
                      <ScriptCard key={s.id} s={s} onUpdate={updateScript} onDelete={deleteScript} />
                    ))}
                  </TabsContent>

                  <TabsContent value="release" className="mt-6 space-y-4">
                    {releases.length === 0 ? (
                      <p className="text-secondary text-sm py-8 text-center">No release analysis yet. Click "Release impact".</p>
                    ) : releases.map((rel) => (
                      <div key={rel.id} className="border border-border p-5" data-testid={`release-${rel.id}`}>
                        <div className="flex items-center justify-between">
                          <h4 className="font-heading font-semibold">{rel.release_name}</h4>
                          <span className="text-xs text-secondary font-mono">{rel.release_date}</span>
                        </div>
                        <p className="text-sm text-secondary mt-2">{rel.summary}</p>
                        {rel.flagged_scripts?.length > 0 && (
                          <p className="mt-2 text-xs text-[#F4511E] flex items-center gap-1" data-testid={`release-flagged-${rel.id}`}>
                            <AlertTriangle className="w-3 h-3" /> {rel.flagged_scripts.length} script{rel.flagged_scripts.length === 1 ? "" : "s"} flagged as impacted — failures in these will be self-healed automatically after the next run.
                          </p>
                        )}
                        <div className="mt-4 space-y-2">
                          {rel.impacted_modules.map((m, i) => (
                            <div key={`${m.module}-${i}`} className="flex items-start gap-3 text-sm border-t border-border pt-2">
                              <span className="w-1.5 h-1.5 mt-1.5 rounded-full shrink-0" style={{ background: RISK[m.risk] || "#3A2DB8" }} />
                              <div>
                                <span className="font-medium">{m.module}</span>
                                <span className="text-secondary"> — {m.change_summary}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </TabsContent>

                  <TabsContent value="config" className="mt-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <Info label="Platform" value={selected.product === "sap_successfactors" ? "SAP SuccessFactors" : "SAP Public Cloud"} />
                      <Info label="Username" value={selected.username_set ? selected.username_preview : "not set"} icon={Lock} />
                      <Info label="Password" value={selected.password_set ? selected.password_preview : "not set"} icon={Lock} />
                      <Info label="API token" value={selected.api_token_set ? selected.api_token_preview : "not set"} icon={Lock} />
                      <Info label="Test data" value={selected.test_data_set ? "encrypted ✓" : "dummy data"} icon={Lock} />
                      <Info label="Dummy data" value={selected.use_dummy_data ? "enabled" : "disabled"} />
                      <Info label="Schedule" value={selected.schedule?.frequency === "off" ? "off (manual only)" : `${selected.schedule?.frequency} @ ${selected.schedule?.time} UTC${selected.schedule?.frequency !== "daily" ? ` · ${selected.schedule?.day}` : ""}`} icon={Clock} />
                      <Info label="Next scheduled run" value={selected.next_run ? selected.next_run.slice(0, 16).replace("T", " ") + " UTC" : "manual"} icon={Clock} />
                      <Info label="Approved scripts" value={`${approvedCount} / ${scripts.length}`} icon={CheckCheck} />
                    </div>
                    {selected.login_form?.has_login_form && (
                      <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4" data-testid="login-form-detected">
                        <p className="text-xs font-semibold text-[#1E1560] mb-2 flex items-center gap-1"><KeyRound className="w-3.5 h-3.5 text-[#2A1E9E]" /> Detected login screen — {selected.login_form.title}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                          {[["username", selected.login_form.username], ["password", selected.login_form.password], ["submit", selected.login_form.submit], ["recovery link", selected.login_form.forgot_link || "—"]].map(([l, v]) => (
                            <div key={l}><span className="text-secondary">{l}: </span><span className="text-[#2A1E9E] break-all">{v}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selected.post_login_scan && (
                      <div className="mt-3 rounded-lg border border-border bg-muted/40 p-4" data-testid="post-login-scan">
                        <p className="text-xs font-semibold text-[#1E1560] mb-1 flex items-center gap-1"><Compass className="w-3.5 h-3.5 text-[#7C3AED]" /> Explored after sign-in — {selected.post_login_scan.title}</p>
                        <p className="text-xs text-secondary font-mono break-all">{selected.post_login_scan.landing_url}</p>
                        <p className="text-xs text-secondary mt-1">{selected.post_login_scan.element_count} interactive elements · {selected.post_login_scan.headings?.slice(0, 6).join(" · ")}</p>
                      </div>
                    )}
                    {selected.schedule?.frequency !== "off" && approvedCount === 0 && (
                      <p className="mt-4 text-xs text-[#FF8A3D] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Scheduled runs will be skipped until at least one script is approved.</p>
                    )}
                    {selected.last_run_at && (
                      <div className="mt-6 flex items-center gap-2 text-sm">
                        {selected.last_pass_rate >= 85 ? <CheckCircle2 className="w-4 h-4 text-[#0F9D77]" /> : <AlertTriangle className="w-4 h-4 text-[#FF8A3D]" />}
                        Last run {selected.last_run_at.slice(0, 16).replace("T", " ")} · {selected.last_pass_rate}% pass
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value, icon: Icon }) => (
  <div className="border border-border p-3">
    <div className="text-[11px] text-secondary flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</div>
    <div className="font-mono text-sm mt-1 break-all">{value}</div>
  </div>
);
